import { useMemo } from "react";
import { Box, Theme, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { canManuallyRecalculateCredit, CreditSurface } from "@vmosolutions/oc-interfaces";
import type {
  ICalculatePlannedCreditResult,
  ICreditClientSettingSetValue,
  ICreditDisplay,
} from "@vmosolutions/oc-interfaces";
import { CreditValueCell } from "./CreditValueCell";
import { RecalculateCreditButton } from "./RecalculateCreditButton";

type ICreditSummaryCardProps = {
  pairingId: string;
  /** True when the editor holds unsaved edits. Blocks the credit recalculation, which reads saved content. */
  pairingDirty: boolean;
  /** Applies the records a credit recalculation wrote. */
  onCreditRecalculated: (result: ICalculatePlannedCreditResult) => void | Promise<void>;
  /** The official Planned calculated credit, one row per module, in client-setting order. */
  plannedRows: ICreditDisplay[];
  /** The latest Current calculated credit, one row per module, same order. */
  currentRows: ICreditDisplay[];
  creditSettingValue: ICreditClientSettingSetValue;
};

type IModuleRow = {
  field: string;
  displayName: string;
  planned?: ICreditDisplay;
  current?: ICreditDisplay;
};

/** Merge the Planned and Current rows into one row per module, Planned's order winning. */
const buildModuleRows = (plannedRows: ICreditDisplay[], currentRows: ICreditDisplay[]): IModuleRow[] => {
  const currentByField = new Map(currentRows.map((row) => [row.plannedCreditField, row]));
  const seen = new Set<string>();
  const rows: IModuleRow[] = plannedRows.map((planned) => {
    seen.add(planned.plannedCreditField);
    return {
      field: planned.plannedCreditField,
      displayName: planned.displayName,
      planned,
      current: currentByField.get(planned.plannedCreditField),
    };
  });
  // A module can have a Current record with no Planned one yet (never built/recalculated) - still show it.
  currentRows
    .filter((current) => !seen.has(current.plannedCreditField))
    .forEach((current) => rows.push({ field: current.plannedCreditField, displayName: current.displayName, current }));
  return rows;
};

/**
 * Planned vs Current calculated credit, one row per module, plus the Recalculate action - which only
 * ever writes the Planned side - in the same card.
 */
export const CreditSummaryCard = (props: ICreditSummaryCardProps): JSX.Element | null => {
  const { pairingId, pairingDirty, onCreditRecalculated, plannedRows, currentRows, creditSettingValue } = props;
  const theme: Theme = useTheme();

  const moduleRows = useMemo(() => buildModuleRows(plannedRows, currentRows), [plannedRows, currentRows]);
  const canRecalculate = canManuallyRecalculateCredit(creditSettingValue);

  // The kill switches hide the card outright - records already written must not keep showing once a
  // customer, or this surface, has been switched back off.
  const master = creditSettingValue?.calculatedCredit;
  if (master?.enabled === false || master?.surfaces?.[CreditSurface.pairingEditor] === false) {
    return null;
  }
  if (moduleRows.length === 0 && !canRecalculate) {
    return null;
  }

  return (
    <Box
      sx={{
        margin: "3px 3px 6px 3px",
        padding: "8px 10px",
        border: `1px solid ${alpha(theme.palette.primary.main, 0.5)}`,
        borderRadius: "4px",
      }}
    >
      <Box
        sx={{
          fontSize: "12px",
          fontWeight: 600,
          marginBottom: "6px",
          color: `${theme.custom.forms.inputText} !important`,
        }}
      >
        Credit summary
      </Box>
      {moduleRows.length > 0 && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "minmax(90px, auto) auto auto",
            columnGap: "16px",
            rowGap: "4px",
            alignItems: "center",
          }}
        >
          <Box sx={{ fontSize: "10px", textTransform: "uppercase", opacity: 0.7 }}>Module</Box>
          {/* Recalculate lives in the Planned header, not a separate top corner - it only ever writes Planned. */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
            <Box sx={{ fontSize: "10px", textTransform: "uppercase", opacity: 0.7 }}>Planned</Box>
            {canRecalculate && (
              <RecalculateCreditButton
                pairingId={pairingId}
                pairingDirty={pairingDirty}
                onRecalculated={onCreditRecalculated}
              />
            )}
          </Box>
          <Box sx={{ fontSize: "10px", textTransform: "uppercase", opacity: 0.7, textAlign: "right" }}>Current</Box>
          {moduleRows.map((row) => (
            <Box key={row.field} sx={{ display: "contents" }}>
              <Box sx={{ fontSize: "12px", whiteSpace: "nowrap", color: `${theme.custom.forms.inputText} !important` }}>
                {row.displayName}
              </Box>
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                {row.planned ? <CreditValueCell row={row.planned} /> : <Box sx={{ opacity: 0.5 }}>-</Box>}
              </Box>
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                {row.current ? <CreditValueCell row={row.current} /> : <Box sx={{ opacity: 0.5 }}>-</Box>}
              </Box>
            </Box>
          ))}
        </Box>
      )}
      {moduleRows.length === 0 && canRecalculate && (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
          <RecalculateCreditButton
            pairingId={pairingId}
            pairingDirty={pairingDirty}
            onRecalculated={onCreditRecalculated}
          />
        </Box>
      )}
      {canRecalculate && (
        <Box sx={{ marginTop: "6px", fontSize: "10px", opacity: 0.6 }}>Recalculate updates planned values only</Box>
      )}
    </Box>
  );
};
